import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { playMiniTaskSound } from '../soundEffects';
import { loadReflexCatalog, REFLEX_VISUAL_ASSETS } from './reflexCatalog';
import { buildReflexWave } from './reflexSequence';

const DEFAULT_REFLEX_DURATION_SECONDS = 45;

function ReflexBackdrop({ assetUrls = {} }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.1), rgba(74,133,24,0.08)), url("${assetUrls[REFLEX_VISUAL_ASSETS.background] || REFLEX_VISUAL_ASSETS.background}")`,
        backgroundPosition: { xs: '50% 50%', md: '50% 42%' },
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 30%, rgba(43,93,17,0.08) 100%)',
        },
      }}
    >
      <Box sx={{ position: 'absolute', left: { xs: 18, md: '8%' }, top: { xs: 250, md: 310 }, color: '#facc15', fontSize: { xs: 25, md: 42 }, transform: 'rotate(-12deg)', textShadow: '0 3px 0 #f59e0b', zIndex: 1 }}>★</Box>
      <Box sx={{ position: 'absolute', right: { xs: 18, md: '9%' }, top: { xs: 330, md: 380 }, color: '#c084fc', fontSize: { xs: 24, md: 36 }, zIndex: 1 }}>✦</Box>
    </Box>
  );
}

function MiniCharacter({ entry, distractor, caught, active, riseMs, fallMs, assetUrls = {} }) {
  const targetPalettes = [
    { accent: '#f97316', badge: '#facc15' },
    { accent: '#7c3aed', badge: '#f9a8d4' },
    { accent: '#16a34a', badge: '#86efac' },
    { accent: '#db2777', badge: '#fbcfe8' },
  ];
  const paletteIndex = Array.from(entry?.assetId || '').reduce((sum, char) => sum + char.charCodeAt(0), 0) % targetPalettes.length;
  const palette = targetPalettes[paletteIndex];
  const isVisible = active && !caught;
  return (
    <Box
      sx={{
        position: 'absolute',
        left: '18%',
        right: '18%',
        bottom: '19%',
        aspectRatio: '1',
        zIndex: 2,
        transform: isVisible ? 'translateY(0) scale(1)' : caught ? 'translateY(8%) scale(0.72)' : 'translateY(112%) scale(0.92)',
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        transition: caught
          ? 'transform 140ms ease-in, opacity 100ms ease, visibility 0ms linear 140ms'
          : active
            ? `transform ${riseMs}ms cubic-bezier(.2,.9,.35,1.18), opacity 70ms ease, visibility 0ms`
            : `transform ${fallMs}ms cubic-bezier(.55,.05,.78,.45), opacity 1ms linear ${fallMs}ms, visibility 0ms linear ${fallMs}ms`,
        pointerEvents: 'none',
        transformOrigin: '50% 100%',
        filter: 'drop-shadow(0 7px 5px rgba(55,28,12,0.2))',
      }}
    >
      <Box
        component="img"
        src={assetUrls[distractor ? REFLEX_VISUAL_ASSETS.sleepy : REFLEX_VISUAL_ASSETS.panda]
          || (distractor ? REFLEX_VISUAL_ASSETS.sleepy : REFLEX_VISUAL_ASSETS.panda)}
        alt=""
        draggable={false}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          userSelect: 'none',
        }}
      />
      <Typography
        aria-hidden
        sx={{
          position: 'absolute',
          right: '-2%',
          top: distractor ? '-9%' : '6%',
          width: '31%',
          height: '31%',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: distractor ? '#dbeafe' : palette.badge,
          color: distractor ? '#1e3a8a' : palette.accent,
          fontWeight: 1000,
          fontSize: 'clamp(12px, 1.7vw, 25px)',
          border: '2px solid rgba(255,255,255,0.96)',
          boxShadow: '0 3px 0 rgba(15,23,42,0.16), 0 5px 12px rgba(15,23,42,0.14)',
        }}
      >
        {entry?.symbol}
      </Typography>
      {distractor && (
        <Typography sx={{ position: 'absolute', left: '-2%', top: '-13%', color: '#2563eb', fontWeight: 1000, fontSize: 'clamp(18px, 3vw, 38px)', transform: 'rotate(-10deg)', textShadow: '0 2px 0 #fff' }}>
          Z
        </Typography>
      )}
    </Box>
  );
}

function Hole({ index, target, distractor, caught, interactive, onTarget, onDistractor, riseMs, fallMs, assetUrls = {} }) {
  const entry = target || distractor;
  const label = target
    ? `${target.label}, yakala`
    : distractor
      ? `${distractor.label}, dokunma`
      : `Boş yuva ${index + 1}`;
  return (
    <ButtonBase
      aria-label={label}
      disabled={!interactive || !entry || caught}
      onClick={target ? onTarget : distractor ? onDistractor : undefined}
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        minHeight: 92,
        aspectRatio: '1.08',
        borderRadius: '46% 46% 38% 38%',
        overflow: 'visible',
        bgcolor: 'transparent',
        transition: 'transform 120ms ease',
        '&:not(:disabled):active': { transform: 'scale(0.96)' },
        '&:focus-visible': { outline: '5px solid #facc15', outlineOffset: 2 },
      }}
    >
      <Box
        component="img"
        src={assetUrls[REFLEX_VISUAL_ASSETS.burrow] || REFLEX_VISUAL_ASSETS.burrow}
        alt=""
        draggable={false}
        sx={{
          position: 'absolute',
          left: '1%',
          right: '1%',
          bottom: '1%',
          width: '98%',
          height: 'auto',
          zIndex: 1,
          pointerEvents: 'none',
          userSelect: 'none',
          filter: 'drop-shadow(0 8px 7px rgba(61,38,15,0.2))',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: '10%',
          right: '10%',
          top: '2%',
          bottom: '9%',
          overflow: 'hidden',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <MiniCharacter
          assetUrls={assetUrls}
          entry={entry}
          distractor={Boolean(distractor)}
          caught={caught}
          active={Boolean(entry)}
          riseMs={riseMs}
          fallMs={fallMs}
        />
      </Box>
      <Box
        component="img"
        src={assetUrls[REFLEX_VISUAL_ASSETS.burrowFront] || REFLEX_VISUAL_ASSETS.burrowFront}
        alt=""
        draggable={false}
        sx={{
          position: 'absolute',
          left: '1%',
          right: '1%',
          bottom: '1%',
          width: '98%',
          height: 'auto',
          zIndex: 3,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
    </ButtonBase>
  );
}

function MetricCard({ type, value }) {
  const isTime = type === 'time';
  return (
    <Box
      sx={{
        minWidth: { xs: 72, sm: 96, md: 128 },
        height: { xs: 52, sm: 58, md: 66 },
        borderRadius: { xs: 2.5, md: 3 },
        border: `2px solid ${isTime ? '#f8c66a' : '#86efac'}`,
        background: isTime
          ? 'linear-gradient(180deg, #fff8dc 0%, #ffedaa 100%)'
          : 'linear-gradient(180deg, #f0fdf4 0%, #c7f3c8 100%)',
        color: isTime ? '#9a4b08' : '#166534',
        px: { xs: 0.8, sm: 1.2, md: 1.6 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 0.35, sm: 0.7, md: 1 },
        boxShadow: `0 5px 0 ${isTime ? 'rgba(180,83,9,0.18)' : 'rgba(21,128,61,0.18)'}, inset 0 2px 0 rgba(255,255,255,0.72)`,
      }}
    >
      {isTime
        ? <AccessTimeRoundedIcon sx={{ fontSize: { xs: 22, sm: 27, md: 34 }, color: '#f97316' }} />
        : <StarRoundedIcon sx={{ fontSize: { xs: 23, sm: 29, md: 37 }, color: '#facc15', filter: 'drop-shadow(0 2px 0 #f59e0b)' }} />}
      <Box sx={{ textAlign: 'left' }}>
        <Typography sx={{ fontWeight: 1000, fontSize: { xs: 10, sm: 12, md: 14 }, lineHeight: 1 }}>
          {isTime ? 'Süre' : 'Skor'}
        </Typography>
        <Typography sx={{ fontWeight: 1000, fontSize: { xs: 21, sm: 26, md: 34 }, lineHeight: 1 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ReflexRoundView({
  round,
  classTitle,
  soundEnabled,
  onToggleSound,
  onExit,
  onComplete,
  assetUrls = {},
}) {
  const [catalog, setCatalog] = useState(null);
  const [phase, setPhase] = useState('intro');
  const [countdown, setCountdown] = useState(3);
  const [remainingSeconds, setRemainingSeconds] = useState(
    Number(round.duration_seconds || DEFAULT_REFLEX_DURATION_SECONDS),
  );
  const [score, setScore] = useState(0);
  const [waveIndex, setWaveIndex] = useState(0);
  const [activeWave, setActiveWave] = useState(null);
  const [caughtKeys, setCaughtKeys] = useState(() => new Set());
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [exitOpen, setExitOpen] = useState(false);
  const completionStartedRef = useRef(false);
  const deadlineRef = useRef(0);
  const scoreRef = useRef(0);
  const interactionCountRef = useRef(0);
  const phaseRef = useRef('intro');
  const waveTimeoutRef = useRef(null);
  const waveVisibleTimeoutRef = useRef(null);
  const clockRef = useRef(null);

  const config = useMemo(() => ({
    seed: round.seed,
    grid_rows: Number(round.grid_rows),
    grid_columns: Number(round.grid_columns),
    target_count_weights: round.target_count_weights,
    distractor_probability_pct: Number(round.distractor_probability_pct),
    gap_min_ms: Number(round.gap_min_ms),
    gap_max_ms: Number(round.gap_max_ms),
  }), [round]);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    let active = true;
    loadReflexCatalog(round.asset_catalog_version)
      .then((payload) => {
        if (active) setCatalog(payload);
      })
      .catch((catalogError) => {
        if (active) setError(catalogError?.message || 'Refleks karakterleri yüklenemedi.');
      });
    return () => {
      active = false;
    };
  }, [round.asset_catalog_version]);

  const clearWaveTimers = useCallback(() => {
    if (waveTimeoutRef.current) window.clearTimeout(waveTimeoutRef.current);
    if (waveVisibleTimeoutRef.current) window.clearTimeout(waveVisibleTimeoutRef.current);
    waveTimeoutRef.current = null;
    waveVisibleTimeoutRef.current = null;
  }, []);

  const completeRound = useCallback(async () => {
    if (completionStartedRef.current) return;
    completionStartedRef.current = true;
    clearWaveTimers();
    if (clockRef.current) window.clearInterval(clockRef.current);
    setActiveWave(null);
    setPhase('completing');
    setError('');
    try {
      await onComplete(scoreRef.current, interactionCountRef.current);
    } catch (completionError) {
      completionStartedRef.current = false;
      setError(completionError?.message || 'Sonucun kaydedilemedi.');
      setPhase('completion_error');
    }
  }, [clearWaveTimers, onComplete]);

  const closeWave = useCallback((wave) => {
    if (phaseRef.current !== 'playing') return;
    if (waveVisibleTimeoutRef.current) window.clearTimeout(waveVisibleTimeoutRef.current);
    setActiveWave(null);
    setCaughtKeys(new Set());
    waveTimeoutRef.current = window.setTimeout(() => {
      setWaveIndex((current) => current + 1);
    }, Number(wave.gapMs));
  }, []);

  useEffect(() => {
    if (phase !== 'intro' || !catalog || error) return undefined;
    const timer = window.setTimeout(() => setPhase('countdown'), 900);
    return () => window.clearTimeout(timer);
  }, [catalog, error, phase]);

  useEffect(() => {
    if (phase !== 'countdown') return undefined;
    if (countdown <= 0) {
      setMessage('Başla!');
      const startTimer = window.setTimeout(() => {
        const durationMs = Number(
          round.duration_seconds || DEFAULT_REFLEX_DURATION_SECONDS,
        ) * 1000;
        deadlineRef.current = performance.now() + durationMs;
        setMessage('');
        setPhase('playing');
      }, 500);
      return () => window.clearTimeout(startTimer);
    }
    const timer = window.setTimeout(() => setCountdown((current) => current - 1), 700);
    return () => window.clearTimeout(timer);
  }, [countdown, phase, round.duration_seconds]);

  useEffect(() => {
    if (phase !== 'playing') return undefined;
    const updateClock = () => {
      const remainingMs = deadlineRef.current - performance.now();
      if (remainingMs <= 0) {
        setRemainingSeconds(0);
        completeRound();
        return;
      }
      setRemainingSeconds(Math.ceil(remainingMs / 1000));
    };
    updateClock();
    clockRef.current = window.setInterval(updateClock, 100);
    const handleVisibility = () => updateClock();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (clockRef.current) window.clearInterval(clockRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [completeRound, phase]);

  useEffect(() => {
    if (phase !== 'playing' || !catalog) return undefined;
    const wave = buildReflexWave(config, waveIndex, catalog);
    setActiveWave(wave);
    setCaughtKeys(new Set());
    waveVisibleTimeoutRef.current = window.setTimeout(() => closeWave(wave), Number(round.visible_ms));
    return () => {
      if (waveVisibleTimeoutRef.current) window.clearTimeout(waveVisibleTimeoutRef.current);
    };
  }, [catalog, closeWave, config, phase, round.visible_ms, waveIndex]);

  useEffect(() => () => {
    clearWaveTimers();
    if (clockRef.current) window.clearInterval(clockRef.current);
  }, [clearWaveTimers]);

  const handleTarget = (holeIndex) => {
    if (phaseRef.current !== 'playing' || !activeWave) return;
    const key = `${activeWave.waveIndex}:${holeIndex}`;
    if (caughtKeys.has(key)) return;
    const nextCaught = new Set(caughtKeys);
    nextCaught.add(key);
    setCaughtKeys(nextCaught);
    interactionCountRef.current += 1;
    scoreRef.current += 1;
    setScore(scoreRef.current);
    setMessage('Pıt!');
    playMiniTaskSound('correct', soundEnabled);
    window.setTimeout(() => setMessage(''), 350);
    if (nextCaught.size === activeWave.targets.length) closeWave(activeWave);
  };

  const handleDistractor = () => {
    if (phaseRef.current !== 'playing') return;
    interactionCountRef.current += 1;
    setMessage('Şşş… bu mini uyuyor.');
    window.setTimeout(() => setMessage(''), 650);
  };

  const retryCompletion = () => completeRound();
  const gridCount = Number(round.grid_rows) * Number(round.grid_columns);
  const targetByHole = new Map((activeWave?.targets || []).map((target) => [target.holeIndex, target]));
  const distractor = activeWave?.distractor || null;
  const isReducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const riseMs = isReducedMotion ? 40 : Number(round.rise_ms);
  const fallMs = isReducedMotion ? 40 : Number(round.fall_ms);
  const gridRows = Number(round.grid_rows);
  const gridColumns = Number(round.grid_columns);
  const instruction = message || (gridRows === 3 ? 'Mini karakterleri yakala!' : 'Yuvadan çıkan mini karakterleri yakala!');

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100svh',
        bgcolor: '#fffaf0',
        color: '#172554',
      }}
    >
      {round.preview && (
        <Box sx={{ position: 'fixed', bottom: 10, left: 10, zIndex: 20, bgcolor: '#facc15', color: '#713f12', px: 1.4, py: 0.45, borderRadius: 99, fontWeight: 1000, boxShadow: '0 3px 0 rgba(146,64,14,0.16)' }}>
          TEST MODU
        </Box>
      )}
      <ReflexBackdrop assetUrls={assetUrls} />
      <Box sx={{
        position: 'relative', zIndex: 1, mx: 'auto', maxWidth: 1720,
        px: { xs: 1.2, sm: 2, md: 3.2 }, pb: { xs: 1, md: 2 },
        boxSizing: 'border-box', height: '100svh', display: 'flex', flexDirection: 'column',
        '& > *': { flexShrink: 0 },
        '--reflex-gap': { xs: '2px', sm: '6px', md: '9.2px' },
        '--reflex-padding': { xs: '4.8px', sm: '9.6px', md: '14.4px' },
      }}>
        <MiniTaskGameHeader isEndless={Boolean(round?.is_endless)} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(108px, 1fr) auto', md: '220px minmax(0, 1fr) auto' },
            alignItems: 'center',
            columnGap: { xs: 0.8, sm: 1.4, md: 2 },
            rowGap: { xs: 0.7, md: 1 },
            mt: { xs: 1.1, sm: 1.5, md: 2 },
            mb: { xs: 0.7, sm: 1, md: 1.2 },
          }}
        >
          <ButtonBase
            onClick={() => setExitOpen(true)}
            disabled={phase === 'completing'}
            sx={{
              justifySelf: 'start',
              gridColumn: 1,
              gridRow: 1,
              borderRadius: 3,
              border: '2px solid #d8b4fe',
              bgcolor: 'rgba(255,255,255,0.9)',
              color: '#33258a',
              px: { xs: 1, sm: 1.5, md: 2.1 },
              py: { xs: 0.7, md: 1 },
              minWidth: { xs: 108, sm: 142, md: 204 },
              minHeight: { xs: 52, md: 60 },
              display: 'flex',
              gap: { xs: 0.4, md: 0.8 },
              boxShadow: '0 4px 0 rgba(91,33,182,0.12)',
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: { xs: 22, md: 29 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 14, sm: 17, md: 23 }, whiteSpace: 'nowrap' }}>Ana sayfa</Typography>
          </ButtonBase>
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 2 }, gridRow: { xs: 2, md: 1 }, textAlign: 'center', alignSelf: 'center' }}>
            <Typography sx={{ color: '#251777', fontWeight: 1000, fontSize: { xs: 29, sm: 38, md: 48 }, lineHeight: 0.98, textShadow: '0 3px 0 rgba(255,255,255,0.94)' }}>Pıt Pıt Yakala</Typography>
            <Typography sx={{ mt: { xs: 0.35, md: 0.55 }, color: '#6d45df', fontWeight: 900, fontSize: { xs: 15, sm: 19, md: 23 }, lineHeight: 1 }}>Refleks Antrenmanı</Typography>
          </Box>
          <Box sx={{ gridColumn: { xs: 2, md: 3 }, gridRow: 1, justifySelf: 'end', display: 'flex', gap: { xs: 0.45, sm: 0.8, md: 1 } }}>
            <MetricCard type="time" value={remainingSeconds} />
            <MetricCard type="score" value={score} />
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: 430, sm: gridRows === 2 ? 860 : 820, lg: gridRows === 2 ? 980 : 940 },
            // Keep the touch-target floor even on unsupported short screens: overflow
            // stays measurable instead of cropping a row or shrinking it out of reach.
            minHeight: `calc(${gridRows * 92 + 64}px + ${gridRows - 1} * var(--reflex-gap) + 2 * var(--reflex-padding))`,
            flex: '1 0 0px',
            containerType: 'size',
            boxSizing: 'border-box',
            mx: 'auto',
            borderRadius: { xs: 4, md: 6 },
            position: 'relative',
            p: 'var(--reflex-padding)',
            display: 'grid',
            placeItems: 'center',
            background: 'radial-gradient(ellipse at 50% 46%, rgba(190,230,91,0.86) 0%, rgba(151,207,65,0.64) 53%, rgba(115,182,58,0.18) 76%, transparent 77%)',
          }}
        >
          {error && phase !== 'completion_error' && (
            <Box sx={{ textAlign: 'center', bgcolor: 'rgba(255,255,255,0.92)', border: '2px solid #fecaca', borderRadius: 4, px: 3, py: 4, boxShadow: '0 12px 26px rgba(76,29,149,0.14)' }}>
              <Typography sx={{ color: '#991b1b', fontWeight: 1000, fontSize: 28 }}>{error}</Typography>
              <Button onClick={onExit} sx={{ mt: 2, fontWeight: 900 }}>Öğrenci listesine dön</Button>
            </Box>
          )}

          {!error && (phase === 'intro' || phase === 'countdown') && (
            <Box sx={{ textAlign: 'center', maxWidth: 680, bgcolor: 'rgba(255,255,255,0.88)', border: '3px solid #e9d5ff', borderRadius: { xs: 4, md: 5 }, px: { xs: 2.2, md: 5 }, py: { xs: 3, md: 4 }, boxShadow: '0 12px 0 rgba(91,33,182,0.10), 0 22px 38px rgba(76,29,149,0.13)' }}>
              <Box sx={{ width: { xs: 82, md: 104 }, height: { xs: 82, md: 104 }, mx: 'auto', mb: 2, borderRadius: '50%', background: 'linear-gradient(180deg, #f5f0ff, #e9d5ff)', color: '#7c3aed', display: 'grid', placeItems: 'center', border: '2px solid #d8b4fe', boxShadow: '0 6px 0 rgba(109,69,223,0.16)' }}>
                <BoltRoundedIcon sx={{ fontSize: { xs: 53, md: 68 } }} />
              </Box>
              <Typography sx={{ color: '#251777', fontWeight: 1000, fontSize: { xs: 28, sm: 38, md: 48 }, lineHeight: 1.12 }}>
                {phase === 'intro' ? 'Yuvadan çıkan mini karakterleri yakala!' : countdown > 0 ? countdown : message}
              </Typography>
            </Box>
          )}

          {(phase === 'completing' || phase === 'completion_error') && (
            <Box sx={{ textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)', border: '3px solid #e9d5ff', borderRadius: 5, px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 4 }, boxShadow: '0 12px 0 rgba(91,33,182,0.10), 0 22px 38px rgba(76,29,149,0.13)' }}>
              {phase === 'completing' ? <CircularProgress size={56} /> : <BoltRoundedIcon sx={{ color: '#f59e0b', fontSize: 72 }} />}
              <Typography sx={{ mt: 2, color: '#251777', fontWeight: 1000, fontSize: { xs: 30, md: 44 } }}>
                {phase === 'completing' ? 'Harika çalıştın!' : 'Sonucun kaydedilemedi'}
              </Typography>
              <Typography sx={{ mt: 1, color: '#6d5d93', fontWeight: 900 }}>{phase === 'completing' ? 'Sonucun kaydediliyor...' : error}</Typography>
              {phase === 'completion_error' && (
                <Button variant="contained" onClick={retryCompletion} sx={{ mt: 2, borderRadius: 99, fontWeight: 1000 }}>Tekrar dene</Button>
              )}
            </Box>
          )}

          {phase === 'playing' && catalog && (
            <Box sx={{ width: '100%', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box
                sx={{
                  // cqh measures the arena left after the actual header/metrics.
                  // Reserve 64px for feedback, then derive width from row count,
                  // gaps and the existing 1.08 hole aspect ratio.
                  width: `min(100%, calc((100cqh - 64px - ${gridRows - 1} * var(--reflex-gap)) * ${gridColumns * 1.08 / gridRows} + ${gridColumns - 1} * var(--reflex-gap)))`,
                  minWidth: `calc(${gridColumns * 92 * 1.08}px + ${gridColumns - 1} * var(--reflex-gap))`,
                  maxWidth: gridRows === 2 ? { sm: 810, lg: 910 } : { xs: 390, sm: 690, md: 760, lg: 830 },
                  mx: 'auto',
                  display: 'grid',
                  gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                  gap: 'var(--reflex-gap)',
                }}
              >
                {Array.from({ length: gridCount }, (_, index) => {
                  const target = targetByHole.get(index) || null;
                  const isDistractor = distractor?.holeIndex === index ? distractor : null;
                  const caught = caughtKeys.has(`${activeWave?.waveIndex}:${index}`);
                  return (
                    <Hole
                      assetUrls={assetUrls}
                      key={index}
                      index={index}
                      target={target}
                      distractor={isDistractor}
                      caught={caught}
                      interactive={Boolean(activeWave)}
                      onTarget={() => handleTarget(index)}
                      onDistractor={handleDistractor}
                      riseMs={riseMs}
                      fallMs={fallMs}
                    />
                  );
                })}
              </Box>
              <Box sx={{ alignSelf: 'center', minHeight: { xs: 42, md: 50 }, mt: { xs: 0.2, sm: 0.5, md: 0.8 }, px: { xs: 1.4, md: 2.4 }, py: { xs: 0.65, md: 0.8 }, borderRadius: 99, bgcolor: message ? '#fff7d6' : 'rgba(255,255,255,0.86)', border: `2px solid ${message ? '#f8c66a' : '#d8b4fe'}`, color: message ? '#a24b0b' : '#4c1d95', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.7, boxShadow: '0 4px 0 rgba(91,33,182,0.10)', transition: 'background-color 140ms ease' }}>
                {message
                  ? <StarRoundedIcon sx={{ color: '#facc15', fontSize: { xs: 22, md: 28 }, filter: 'drop-shadow(0 2px 0 #f59e0b)' }} />
                  : <FavoriteRoundedIcon sx={{ color: '#a78bfa', fontSize: { xs: 19, md: 24 } }} />}
                <Typography sx={{ textAlign: 'center', fontWeight: 1000, fontSize: { xs: 14, sm: 17, md: 20 }, lineHeight: 1 }}>
                  {instruction}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={exitOpen} onClose={() => setExitOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, border: '2px solid #e9d5ff' } }}>
        <DialogTitle sx={{ color: '#251777', fontWeight: 1000, fontSize: 26 }}>Görevden çıkmak istiyor musun?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6d5d93', fontWeight: 800 }}>Bu turdaki ilerlemen kaydedilmeyecek.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExitOpen(false)} autoFocus sx={{ fontWeight: 900 }}>Göreve dön</Button>
          <Button color="error" variant="contained" onClick={onExit} sx={{ borderRadius: 99, fontWeight: 900 }}>Çık</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
